import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { showSuccess } from "@/lib/utils/notifications";
import { EditNoteModal } from "@/components/notes/EditNoteModal";
import { getNoteRowId } from "../../utils/notesTabHelpers";
import { CreditReportAddressModal } from "../modals/CreditReportAddressModal";
import type { CreditReportTabModalsProps } from "../../types";
import { resolveCreditReportHistory } from "../../utils/creditReportHelpers";

export function CreditReportTabModals({ tab }: CreditReportTabModalsProps) {
  const {
    applicantId,
    applicationData,
    applicationAllData,
    editingUnderwriterNote,
    performResubmitZipReportPrimary,
    performResubmitZipReportSecondary,
    performSaveScore,
    refreshAfterAction,
    refreshUnderwriterNotes,
    resubmitConfirm,
    saveScoreConfirm,
    setEditingUnderwriterNote,
    setSaveScoreConfirm,
    setShowAddressModal,
    showAddressModal,
  } = tab;

  return (
    <>
      <EditNoteModal
        isOpen={!!editingUnderwriterNote}
        notesId={editingUnderwriterNote ? getNoteRowId(editingUnderwriterNote) : null}
        initialNotes={String(
          editingUnderwriterNote?.notes ??
            editingUnderwriterNote?.note ??
            editingUnderwriterNote?.note_text ??
            "",
        )}
        onClose={() => setEditingUnderwriterNote(null)}
        onSaved={() => {
          showSuccess("Note Edited Successfully!");
          void refreshUnderwriterNotes();
        }}
        testIdPrefix="application-credit-report-tab-uw-edit-note"
      />

      <CreditReportAddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        applicantId={applicantId}
        creditReportFiles={
          typeof applicationData?.credit_report_files === "string"
            ? applicationData.credit_report_files
            : undefined
        }
        savedAlternateAddresses={
          Array.isArray(applicationData?.alternate_address)
            ? applicationData.alternate_address
            : undefined
        }
        creditReportHistory={resolveCreditReportHistory(applicationData, applicationAllData)}
        onDone={refreshAfterAction}
      />

      <ConfirmDialog
        isOpen={resubmitConfirm.open}
        overlayDataTestId="application-credit-report-tab-resubmit-confirm-overlay"
        primaryButtonDataTestId="application-credit-report-tab-resubmit-confirm-primary-button"
        secondaryButtonDataTestId="application-credit-report-tab-resubmit-confirm-secondary-button"
        closeButtonDataTestId="application-credit-report-tab-resubmit-confirm-close-button"
        title="Resubmit Credit Report"
        message={resubmitConfirm.message}
        primaryLabel={resubmitConfirm.accessExisting ? "Access existing" : "Resubmit"}
        onPrimary={() => void performResubmitZipReportPrimary()}
        secondaryLabel={resubmitConfirm.accessExisting ? "Generate new" : "Cancel"}
        onSecondary={() => void performResubmitZipReportSecondary()}
        variant="warning"
      />

      <ConfirmDialog
        isOpen={saveScoreConfirm.open}
        overlayDataTestId="application-credit-report-tab-save-score-confirm-overlay"
        primaryButtonDataTestId="application-credit-report-tab-save-score-confirm-primary-button"
        secondaryButtonDataTestId="application-credit-report-tab-save-score-confirm-secondary-button"
        closeButtonDataTestId="application-credit-report-tab-save-score-confirm-close-button"
        title="Save Score"
        message={`Do you want to save ${saveScoreConfirm.score} score for applicant?`}
        primaryLabel="Save"
        onPrimary={() => void performSaveScore()}
        secondaryLabel="Cancel"
        primaryFirst
        onSecondary={() => setSaveScoreConfirm({ open: false, score: "" })}
        variant="warning"
      />
    </>
  );
}
